import React, { useState, useRef, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { 
  X, Upload, User, Mail, Phone, Calendar, MapPin, 
  Shield, Lock, Eye, EyeOff, AlertCircle, Check, Sparkles
} from 'lucide-react';

const UserFormModalComplete = ({ isOpen, onClose, onSubmit, user = null, onResetPassword }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: 'M',
    address: '',
    city: '',
    role: 'user',
    password: '',
    confirmPassword: '',
    photo: null,
    generatedPassword: null
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        date_of_birth: user.date_of_birth || '',
        gender: user.gender || 'M',
        address: user.address || '',
        city: user.city || '',
        role: user.role || 'user',
        password: '',
        confirmPassword: '',
        photo: null,
        generatedPassword: null
      });
      if (user.photo_url) {
        setPreviewImage(`${process.env.REACT_APP_API_URL?.replace('/api', '')}${user.photo_url}`);
      }
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleFileChange = (e) => {
    e.stopPropagation();
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("La taille de l'image ne doit pas dépasser 5MB"); return; }
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) { setError("Seuls les formats JPG et PNG sont acceptés"); return; }
    const reader = new FileReader();
    reader.onloadend = () => { setPreviewImage(reader.result); setFormData(prev => ({ ...prev, photo: file })); };
    reader.readAsDataURL(file);
  };

  const goToNextStep = () => { if (currentStep < 3) { setCurrentStep(prev => prev + 1); setError(''); } };
  const goToPreviousStep = () => { if (currentStep > 1) { setCurrentStep(prev => prev - 1); setError(''); } };

  const validateForm = () => {
    if (!formData.first_name?.trim()) { setError('Le prénom est obligatoire'); setCurrentStep(1); return false; }
    if (!formData.last_name?.trim()) { setError('Le nom est obligatoire'); setCurrentStep(1); return false; }
    if (!formData.email?.trim()) { setError("L'email est obligatoire"); setCurrentStep(1); return false; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) { setError("Format d'email invalide"); setCurrentStep(1); return false; }
    if (!formData.phone?.trim()) { setError('Le téléphone est obligatoire'); setCurrentStep(1); return false; }

    if (!user) {
      if (!formData.password) { setError('Le mot de passe est obligatoire'); setCurrentStep(2); return false; }
      if (formData.password.length < 8) { setError('Le mot de passe doit contenir au moins 8 caractères'); setCurrentStep(2); return false; }
      if (formData.password !== formData.confirmPassword) { setError('Les mots de passe ne correspondent pas'); setCurrentStep(2); return false; }
    } else if (formData.password) {
      if (formData.password.length < 8) { setError('Le mot de passe doit contenir au moins 8 caractères'); setCurrentStep(2); return false; }
      if (!formData.generatedPassword && formData.password !== formData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas'); setCurrentStep(2); return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (loading) return;
    if (!validateForm()) return;
    try {
      setLoading(true); setError('');
      const submitData = {
        name: `${formData.first_name.trim()} ${formData.last_name.trim()}`,
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
        phone: formData.phone?.trim() || null,
        date_of_birth: formData.date_of_birth || null,
        gender: formData.gender,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null
      };
      if (formData.password) submitData.password = formData.password;
      if (formData.photo) submitData.photo = formData.photo;
      await onSubmit(submitData);
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    base: "w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm",
    focus: (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(68,134,150,0.1)'; },
    blur:  (e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; },
  };

  const renderError = () => !error ? null : (
    <div className="p-4 rounded-xl border-2 flex items-start gap-3 mb-4" style={{ backgroundColor: '#fff5e5', borderColor: '#fed774' }}>
      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fed774' }}>
        <AlertCircle size={18} className="text-gray-900" />
      </div>
      <div>
        <p className="font-semibold text-gray-900">Attention</p>
        <p className="text-sm text-gray-700">{error}</p>
      </div>
    </div>
  );

  const stepLabels = ['Informations', 'Compte', 'Photo'];

  // ============================================================================
  // ÉTAPE 1
  // ============================================================================
  const renderStep1 = () => (
    <div className="space-y-4">
      {renderError()}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-3 flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
          <User size={20} className="text-white" />
          <h3 className="font-bold text-white">Informations personnelles</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Prénom <span className="text-red-500">*</span></label>
              <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange}
                className={inp.base} onFocus={inp.focus} onBlur={inp.blur} placeholder="Jean" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Nom <span className="text-red-500">*</span></label>
              <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange}
                className={inp.base} onFocus={inp.focus} onBlur={inp.blur} placeholder="Dupont" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Sexe <span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {['M', 'F'].map(g => (
                  <button key={g} type="button" onClick={() => setFormData(prev => ({ ...prev, gender: g }))}
                    className="flex-1 px-4 py-3 rounded-xl transition-all font-semibold text-sm"
                    style={{ backgroundColor: formData.gender === g ? '#448696' : '#fff', color: formData.gender === g ? '#fff' : '#374151', border: formData.gender === g ? 'none' : '1px solid #e5e5e5' }}>
                    {g === 'M' ? 'Masculin' : 'Féminin'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Date de naissance</label>
              <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleInputChange}
                className={inp.base} onFocus={inp.focus} onBlur={inp.blur} />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Email <span className="text-red-500">*</span></label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} disabled={!!user}
                className={`${inp.base} disabled:bg-gray-100 disabled:cursor-not-allowed`} onFocus={inp.focus} onBlur={inp.blur} placeholder="nom@entreprise.com" />
              {user && <p className="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Téléphone <span className="text-red-500">*</span></label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                className={inp.base} onFocus={inp.focus} onBlur={inp.blur} placeholder="+242 06..." />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Adresse</label>
              <input type="text" name="address" value={formData.address} onChange={handleInputChange}
                className={inp.base} onFocus={inp.focus} onBlur={inp.blur} placeholder="123 Rue de la Paix" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Ville</label>
              <input type="text" name="city" value={formData.city} onChange={handleInputChange}
                className={inp.base} onFocus={inp.focus} onBlur={inp.blur} placeholder="Brazzaville" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // ÉTAPE 2
  // ============================================================================
  const renderStep2 = () => (
    <div className="space-y-4">
      {renderError()}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-3 flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
          <Shield size={20} className="text-white" />
          <h3 className="font-bold text-white">Compte et accès</h3>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-1.5">Rôle <span className="text-red-500">*</span></label>
            <select name="role" value={formData.role} onChange={handleInputChange}
              disabled={!!user?.is_primary_admin}
              className={`${inp.base} appearance-none cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed`}
              onFocus={inp.focus} onBlur={inp.blur}>
              <option value="user">Utilisateur</option>
              <option value="manager">Manager</option>
              <option value="admin">Administrateur</option>
            </select>
            {!!user?.is_primary_admin && (
              <p className="text-xs text-gray-500 mt-1">Le rôle de l'admin principal ne peut pas être modifié</p>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-1.5">
              Mot de passe {!user && <span className="text-red-500">*</span>}
            </label>
            {!user ? (
              <div className="space-y-3">
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} name="password"
                    value={formData.password} onChange={handleInputChange}
                    className={`${inp.base} pr-12`} onFocus={inp.focus} onBlur={inp.blur} placeholder="••••••••" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <input type={showPassword ? 'text' : 'password'} name="confirmPassword"
                  value={formData.confirmPassword} onChange={handleInputChange}
                  className={inp.base} onFocus={inp.focus} onBlur={inp.blur} placeholder="Confirmer le mot de passe" />
              </div>
            ) : (
              <div>
                {!formData.generatedPassword ? (
                  <button type="button"
                    onClick={async () => {
                      const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
                      let pwd = '';
                      for (let i = 0; i < 10; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
                      setFormData(prev => ({ ...prev, password: pwd, generatedPassword: pwd }));
                      if (user && onResetPassword) await onResetPassword(user.id, pwd);
                    }}
                    className="w-full px-4 py-3 rounded-xl border-2 border-dashed font-semibold text-sm transition-all"
                    style={{ borderColor: '#448696', color: '#448696', background: 'rgba(68,134,150,0.04)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(68,134,150,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(68,134,150,0.04)'}>
                    🔑 Générer et enregistrer un mot de passe temporaire
                  </button>
                ) : (
                  <div style={{ padding: '14px 16px', borderRadius: 12, background: '#f0fdf4',
                    border: '1.5px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 11, color: '#059669', fontWeight: 700, margin: '0 0 4px',
                        textTransform: 'uppercase', letterSpacing: '0.5px' }}>✓ Mot de passe temporaire enregistré</p>
                      <p style={{ fontSize: 17, fontWeight: 800, fontFamily: 'monospace',
                        color: '#0f172a', letterSpacing: '2px', margin: 0 }}>
                        {formData.generatedPassword}
                      </p>
                      <p style={{ fontSize: 11, color: '#6b7280', margin: '4px 0 0' }}>
                        L'utilisateur devra le changer à la prochaine connexion
                      </p>
                    </div>
                    <button type="button" onClick={() => navigator.clipboard.writeText(formData.generatedPassword)}
                      style={{ padding: '7px 12px', borderRadius: 8, border: 'none',
                        background: '#059669', color: 'white', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                      Copier
                    </button>
                    <button type="button"
                      onClick={() => setFormData(prev => ({ ...prev, password: '', generatedPassword: null }))}
                      style={{ padding: 6, borderRadius: 8, border: 'none', background: 'transparent',
                        color: '#94a3b8', cursor: 'pointer', flexShrink: 0, fontSize: 16 }}>
                      ✕
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // ÉTAPE 3
  // ============================================================================
  const renderStep3 = () => (
    <div className="space-y-4">
      {renderError()}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-3 flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
          <Upload size={20} className="text-gray-900" />
          <h3 className="font-bold text-gray-900">Photo de profil (optionnel)</h3>
        </div>
        <div className="p-5">
          <div className="flex justify-center px-6 py-8 border-2 border-dashed rounded-xl" style={{ borderColor: '#e5e5e5' }}>
            {previewImage ? (
              <div className="relative">
                <img src={previewImage} alt="Preview" className="max-h-52 rounded-xl shadow-lg" />
                <button type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPreviewImage(null); setFormData(prev => ({ ...prev, photo: null })); }}
                  className="absolute -top-3 -right-3 p-2 rounded-full shadow-lg transition-all"
                  style={{ backgroundColor: '#fed774' }}>
                  <X size={18} className="text-gray-900" />
                </button>
              </div>
            ) : (
              <div className="space-y-3 text-center">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}>
                  <Upload size={28} style={{ color: '#448696' }} />
                </div>
                <label htmlFor="photo" className="cursor-pointer font-semibold text-base hover:opacity-80 transition-opacity inline-block"
                  style={{ color: '#448696' }} onClick={e => e.stopPropagation()}>
                  Choisir une photo
                </label>
                <input id="photo" type="file" accept="image/jpeg,image/jpg,image/png"
                  ref={fileInputRef} onChange={handleFileChange} onClick={e => e.stopPropagation()} className="hidden" />
                <p className="text-sm text-gray-500">JPG ou PNG · max 5MB</p>
              </div>
            )}
          </div>
          <div className="mt-3 p-3 rounded-xl text-center" style={{ backgroundColor: '#f0f9fb' }}>
            <p className="text-sm font-medium text-gray-600">
              Cliquez sur <span className="font-bold" style={{ color: '#448696' }}>{user ? '"Enregistrer"' : '"Créer l\'utilisateur"'}</span> pour finaliser
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Dialog.Root open={isOpen} onOpenChange={() => {}}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.6)', zIndex: 9998 }} />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl max-h-[95vh] rounded-2xl shadow-2xl border border-gray-100 flex flex-col"
          style={{ backgroundColor: '#fdfcf5', zIndex: 9999 }}
        >

          {/* ── HEADER FIXE ── */}
          <div className="flex-shrink-0 px-6 py-4 flex items-center justify-between rounded-t-2xl" style={{ backgroundColor: '#448696' }}>
            <Dialog.Title className="text-xl font-bold text-white">
              {user ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
            </Dialog.Title>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white">
              <X size={20} />
            </button>
          </div>

          {/* ── STEPPER FIXE ── */}
          <div className="flex-shrink-0 px-6 py-4 bg-[#fdfcf5] border-b border-gray-100">
            <div className="flex items-center justify-between max-w-lg mx-auto">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className="w-10 h-10 flex items-center justify-center font-bold rounded-full transition-all"
                      style={{ background: currentStep >= step ? '#448696' : '#e5e7eb', color: currentStep >= step ? 'white' : '#9ca3af' }}
                    >
                      {currentStep > step ? <Check size={18} /> : step}
                    </div>
                    <span className="text-xs font-medium text-gray-500 mt-1">{stepLabels[step - 1]}</span>
                  </div>
                  {step < 3 && (
                    <div className="flex-1 h-1 mx-2 rounded-full" style={{ backgroundColor: currentStep > step ? '#448696' : '#e5e7eb' }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── CONTENU SCROLLABLE ── */}
          <div className="flex-1 overflow-y-auto modal-scroll min-h-0 p-5">
            <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }} noValidate>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
            </form>
          </div>

          {/* ── FOOTER FIXE ── */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex justify-end gap-3 rounded-b-2xl bg-gray-50">
            {currentStep > 1 ? (
              <button type="button" onClick={goToPreviousStep}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all">
                Précédent
              </button>
            ) : (
              <button type="button" onClick={onClose}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all">
                Annuler
              </button>
            )}

            {currentStep < 3 ? (
              <button type="button" onClick={goToNextStep}
                className="px-6 py-2.5 text-white font-semibold rounded-xl transition-all shadow-lg"
                style={{ backgroundColor: '#448696' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#367080'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#448696'}>
                Suivant
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={loading}
                className="px-6 py-2.5 text-gray-900 font-semibold rounded-xl transition-all shadow-lg disabled:opacity-50"
                style={{ backgroundColor: '#fed774' }}
                onMouseEnter={e => !loading && (e.currentTarget.style.backgroundColor = '#fdd24e')}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fed774'}>
                {loading ? 'Enregistrement...' : user ? 'Enregistrer' : "Créer l'utilisateur"}
              </button>
            )}
          </div>

        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default UserFormModalComplete;